import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Descriptions, Result, Row, Space, Tag, Typography } from 'antd';
import type { WorkHistoryListItem } from '../../types/work-history';
import { useWorkHistoryDetail } from '../../hooks/useWorkHistory';
import { useAuthStore } from '../../stores/authStore';
import { DateDisplay } from '../../components/common';

const { Title, Paragraph, Text } = Typography;

interface LocationState {
  record?: WorkHistoryListItem;
}

const STATUS_TAG: Record<string, { color: string; label: string }> = {
  completed: { color: 'green', label: '完了' },
  incomplete: { color: 'orange', label: '未完了' },
  cancelled: { color: 'red', label: 'キャンセル' }
};

export default function WorkHistoryDetailPage(): JSX.Element {
  const location = useLocation();
  const locationState = (location.state as LocationState | undefined) ?? {};
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();

  const presetRecord = locationState.record;

  const { record } = useWorkHistoryDetail(
    {
      companyId: user?.companyId,
      displayId: presetRecord?.displayId ?? id,
      search: presetRecord ? undefined : id
    },
    {
      enabled: Boolean(!presetRecord && user?.companyId && id)
    }
  );

  const resolvedRecord = presetRecord ?? record;

  if (!resolvedRecord) {
    return (
      <Result
        status="info"
        title="作業履歴が見つかりません"
        subTitle="一覧画面からアクセスした場合に詳細を表示できます。"
        extra={
          <Button type="primary" onClick={() => navigate('/app/work-history')}>
            作業履歴一覧に戻る
          </Button>
        }
      />
    );
  }

  const status = STATUS_TAG[resolvedRecord.status] ?? {
    color: 'default' as const,
    label: resolvedRecord.status
  };
  const actualHoursValue = Number(resolvedRecord.actualHours);
  const actualHoursLabel = Number.isFinite(actualHoursValue)
    ? actualHoursValue.toFixed(1)
    : '-';

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Space>
        <Button onClick={() => navigate(-1)}>戻る</Button>
        <Button type="primary" onClick={() => window.print()}>
          PDF出力
        </Button>
      </Space>
      <Card>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Title level={3} style={{ marginBottom: 0 }}>
            {resolvedRecord.workTitle}
          </Title>
          <Space>
            <Tag color="blue">ID: {resolvedRecord.displayId}</Tag>
            <Tag color={status.color}>{status.label}</Tag>
            {resolvedRecord.result && (
              <Tag color="purple">結果: {resolvedRecord.result}</Tag>
            )}
          </Space>
          {resolvedRecord.workSummary && (
            <Paragraph>{resolvedRecord.workSummary}</Paragraph>
          )}
        </Space>
      </Card>

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title="基本情報" variant="borderless">
            <Descriptions column={1} colon>
              <Descriptions.Item label="設備">
                {resolvedRecord.equipmentId}
              </Descriptions.Item>
              <Descriptions.Item label="作業カテゴリ">
                {resolvedRecord.workType}
              </Descriptions.Item>
              <Descriptions.Item label="実施者">
                {resolvedRecord.performedBy ?? '未入力'}
              </Descriptions.Item>
              <Descriptions.Item label="承認者">
                {resolvedRecord.approvedBy ?? '未承認'}
              </Descriptions.Item>
              <Descriptions.Item label="開始日時">
                <DateDisplay value={resolvedRecord.startTime} showTime />
              </Descriptions.Item>
              <Descriptions.Item label="終了日時">
                <DateDisplay value={resolvedRecord.endTime} showTime />
              </Descriptions.Item>
              <Descriptions.Item label="実績時間 (h)">
                {actualHoursLabel}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="備考" variant="borderless">
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div>
                <Text strong>所見</Text>
                <Paragraph>
                  {resolvedRecord.findings ?? '記録なし'}
                </Paragraph>
              </div>
              <div>
                <Text strong>推奨事項</Text>
                <Paragraph>
                  {resolvedRecord.recommendations ?? '記録なし'}
                </Paragraph>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="作業指示書ID">
            {resolvedRecord.workOrderId ?? 'なし'}
          </Descriptions.Item>
          <Descriptions.Item label="作成日時">
            <DateDisplay value={resolvedRecord.createdAt} showTime />
          </Descriptions.Item>
          <Descriptions.Item label="更新日時">
            <DateDisplay value={resolvedRecord.updatedAt} showTime />
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="チェックリスト結果" size="small">
        <Text type="secondary">
          チェックリストの詳細は現在のデータソースで提供されていません。必要に応じて一覧画面のモーダルから確認できます。
        </Text>
      </Card>
    </Space>
  );
}
